import React from 'react';
import { Info, Clock, Calculator, PiggyBank, Zap, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о пенсионной системе РФ и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Пенсионная система России: основные понятия и рекомендации
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-emerald-500" />
          Пенсионная система Российской Федерации
        </h3>
        <p>
          Пенсионная система России включает в себя <strong>обязательное пенсионное страхование</strong> и <strong>добровольное пенсионное обеспечение</strong>. С 2022 года функции Пенсионного фонда России (ПФР) выполняет Социальный фонд России (СФР).
        </p>
        <p>
          <strong>Страховая пенсия</strong> формируется за счет страховых взносов, которые работодатель уплачивает за своих сотрудников в Социальный фонд России (22% от заработной платы в пределах установленной базы), а также дополнительных отчислений государства.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-emerald-500" />
          Как формируется пенсия
        </h3>
        <p>
          Страховая пенсия рассчитывается по формуле:
        </p>
        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg font-mono">
          СП = ИПК × СПК + ФВ
        </div>
        <ul>
          <li><strong>СП</strong> — размер страховой пенсии</li>
          <li><strong>ИПК</strong> — сумма индивидуальных пенсионных коэффициентов (баллов)</li>
          <li><strong>СПК</strong> — стоимость одного пенсионного коэффициента (в 2025 году — 116,2 руб.)</li>
          <li><strong>ФВ</strong> — фиксированная выплата (в 2025 году — 7567,33 руб.)</li>
        </ul>

        <h4>Индивидуальный пенсионный коэффициент (ИПК)</h4>
        <p>
          ИПК формируется из взносов, которые поступают на вашу страховую пенсию. Ежегодно можно заработать не более 10 баллов. Они начисляются исходя из суммы страховых взносов, уплаченных работодателем, и зависят от размера заработной платы.
        </p>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-emerald-500" />
          Накопительная пенсия
        </h3>
        <p>
          <strong>Накопительная пенсия</strong> формируется за счет добровольных отчислений и инвестиционного дохода. С 2014 года формирование накопительной пенсии в системе обязательного пенсионного страхования приостановлено ("заморожено").
        </p>
        <p>
          Существуют следующие способы формирования пенсионных накоплений:
        </p>
        <ul>
          <li>
            <strong>Негосударственные пенсионные фонды (НПФ)</strong>: Добровольные взносы в НПФ формируют дополнительную пенсию. НПФ инвестируют средства для получения дохода.
          </li>
          <li>
            <strong>Программы страховых компаний</strong>: Накопительное страхование жизни с пенсионными опциями.
          </li>
          <li>
            <strong>Индивидуальные инвестиции</strong>: Самостоятельное инвестирование через брокерский счет или индивидуальный инвестиционный счет (ИИС).
          </li>
          <li>
            <strong>Государственная программа софинансирования</strong>: Программа добровольных взносов с участием государства (для тех, кто вступил до 2015 года).
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Ключевые показатели пенсионной системы РФ
        </h3>
        <ul>
          <li><strong>Пенсионный возраст</strong>: Для мужчин — 65 лет, для женщин — 60 лет (с 2023 года).</li>
          <li><strong>Минимальный стаж</strong>: 15 лет (необходимый для получения страховой пенсии).</li>
          <li><strong>Минимальное количество пенсионных баллов</strong>: 30 баллов (необходимое для страховой пенсии).</li>
          <li><strong>Средняя страховая пенсия по старости</strong>: 22 574 руб. (на 2025 год).</li>
          <li><strong>Социальная пенсия</strong>: 7 тыс. - 11 тыс. руб. (на 2025 год, в зависимости от региона).</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Как увеличить будущую пенсию?</h4>
          <ol className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            <li>Официальное трудоустройство и "белая" зарплата</li>
            <li>Максимально длительный страховой стаж</li>
            <li>Отложенный выход на пенсию (премиальные коэффициенты)</li>
            <li>Формирование накоплений в НПФ или через ИИС</li>
            <li>Инвестирование в различные финансовые инструменты</li>
          </ol>
        </div>

        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-500" />
          Особые категории и льготы
        </h3>
        <p>
          В России существуют особые правила назначения пенсий для отдельных категорий граждан:
        </p>
        <ul>
          <li>
            <strong>Досрочные пенсии</strong> для работников вредных и опасных производств (список №1 и №2), медицинских работников, педагогов и т.д.
          </li>
          <li>
            <strong>Северный стаж</strong>: Граждане, проработавшие в районах Крайнего Севера и приравненных к ним местностях, имеют право на досрочный выход на пенсию и повышенный размер выплат.
          </li>
          <li>
            <strong>Сельские надбавки</strong>: Доплата 25% к фиксированной выплате для сельских жителей с 30-летним стажем работы в сельском хозяйстве.
          </li>
          <li>
            <strong>Региональные доплаты</strong>: Во многих регионах России устанавливаются доплаты к пенсии до регионального прожиточного минимума пенсионера.
          </li>
        </ul>

        <h3>Материнский капитал и пенсионные права</h3>
        <p>
          Период ухода за ребенком включается в страховой стаж, за него начисляются пенсионные баллы:
        </p>
        <ul>
          <li>1,8 балла за год ухода за первым ребенком</li>
          <li>3,6 балла за год ухода за вторым ребенком</li>
          <li>5,4 балла за год ухода за третьим и последующими детьми</li>
        </ul>
        <p>
          Средства материнского капитала можно направить на формирование накопительной пенсии матери.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Что снижает размер будущей пенсии
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Неофициальное трудоустройство и "серая" зарплата</li>
            <li>Недостаточный страховой стаж</li>
            <li>Низкая заработная плата</li>
            <li>Отсутствие дополнительных накоплений</li>
            <li>Ранний выход на пенсию без льготных оснований</li>
          </ul>
        </div>

        <h3>Советы по планированию пенсии</h3>
        <ol>
          <li><strong>Начинайте рано</strong> формировать пенсионные накопления — чем раньше начнете, тем больше времени для роста инвестиций.</li>
          <li><strong>Диверсифицируйте</strong> источники пенсионного дохода, не полагайтесь только на государственную пенсию.</li>
          <li><strong>Регулярно проверяйте</strong> состояние своего лицевого счета в СФР через Госуслуги.</li>
          <li><strong>Правильно выбирайте НПФ</strong>, обращая внимание на историю доходности, надежность и прозрачность.</li>
          <li><strong>Используйте налоговые льготы</strong> при формировании накоплений (ИИС тип А или тип Б).</li>
          <li><strong>Учитывайте инфляцию</strong> при планировании — выбирайте инструменты с доходностью выше инфляции.</li>
          <li><strong>Постепенно снижайте риски</strong> инвестиций с приближением к пенсионному возрасту.</li>
        </ol>

        <h3>Как контролировать формирование своей пенсии</h3>
        <ul>
          <li>
            <strong>Личный кабинет на Госуслугах</strong>: Проверка сведений о стаже, начисленных баллах и страховых взносах.
          </li>
          <li>
            <strong>Выписка из индивидуального лицевого счета</strong>: Заказ через МФЦ, офис СФР или на официальном сайте СФР.
          </li>
          <li>
            <strong>Персональный пенсионный калькулятор</strong>: Расчет примерного размера будущей пенсии на сайте СФР.
          </li>
          <li>
            <strong>Контроль работодателя</strong>: Убедитесь, что работодатель перечисляет страховые взносы.
          </li>
        </ul>

        <p className="font-semibold mt-6">
          Наш пенсионный калькулятор поможет вам оценить размер будущей пенсии и спланировать необходимые действия для обеспечения комфортной жизни на пенсии!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;